<script lang="ts">
  // The reader's More menu (spec §10): every item in order, positioned by `placeMenu` (220 px,
  // anchored to the button, flips above, 8 px margins, RTL-aware), transparent scrim, focus trap,
  // Arrow/Home/End navigation, Escape closes (document capture so the shell never sees it).
  import { untrack } from 'svelte';
  import { t } from '$lib/i18n';
  import { focusTrap, MENU_MARGIN, menuKeys, menuPos, placeMenu, stopArrowKeys, type MenuAnchor } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';
  import type { MenuAction } from './_local';

  let {
    anchor,
    flagged,
    fromMe,
    isJunk,
    srcUrl,
    dlUrl,
    onclose,
    onaction,
  }: {
    anchor: MenuAnchor;
    flagged: boolean;
    fromMe: boolean;
    isJunk: boolean;
    srcUrl: string;
    dlUrl: string;
    onclose: () => void;
    onaction: (a: MenuAction) => void;
  } = $props();

  // Initial geometry from the anchor (height unknown yet); `menuPos` refines it once measured.
  let pos = $state(untrack(() => placeMenu(anchor)));
  const maxH = typeof window !== 'undefined' ? Math.max(120, window.innerHeight - 2 * MENU_MARGIN) : 400;

  function pick(a: MenuAction): void {
    onclose();
    onaction(a);
  }

  $effect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      ev.stopPropagation();
      onclose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  });
</script>

<div class="menu-scrim" role="presentation" aria-hidden="true" onclick={onclose}></div>
<div
  class="menu"
  role="menu"
  style="top:{pos.top}px;left:{pos.left}px;max-height:{maxH}px"
  use:menuPos={{ anchor, set: (p) => (pos = p) }}
  use:menuKeys
  use:stopArrowKeys
  use:focusTrap
>
  <button type="button" role="menuitem" class="mi-replyall" onclick={() => pick('replyAll')}><Icon name="reply" size={16} /> {t('replyAll')}</button>
  <button type="button" role="menuitem" class="mi-forward" onclick={() => pick('forward')}><Icon name="forward" size={16} /> {t('forward')}</button>
  <button type="button" role="menuitem" class="mi-star" class:on={flagged} onclick={() => pick('star')}><Icon name="star" size={16} /> {t(flagged ? 'unstar' : 'star')}</button>
  <div class="menu-sep"></div>
  <button type="button" role="menuitem" class="mi-unread" onclick={() => pick('markUnread')}><Icon name="inbox" size={16} /> {t('markUnread')}</button>
  <button type="button" role="menuitem" class="mi-move" onclick={() => pick('move')}><Icon name="move" size={16} /> {t('move')}</button>
  <button type="button" role="menuitem" class="mi-junk" onclick={() => pick('junk')}><Icon name="junk" size={16} /> {t(isJunk ? 'notJunk' : 'junk')}</button>
  {#if fromMe}
    <button type="button" role="menuitem" class="mi-editnew" onclick={() => pick('editAsNew')}><Icon name="pen" size={16} /> {t('editAsNew')}</button>
  {:else}
    <button type="button" role="menuitem" class="mi-addcontact" onclick={() => pick('addSenderContact')}><Icon name="users" size={16} /> {t('addSenderContact')}</button>
  {/if}
  <button type="button" role="menuitem" class="mi-fromsender" onclick={() => pick('fromSender')}><Icon name="users" size={16} /> {t('fromSender')}</button>
  <button type="button" role="menuitem" class="mute-item" onclick={() => pick('muteThread')}><Icon name="moon" size={16} /> {t('muteThread')}</button>
  <button type="button" role="menuitem" class="mi-print" onclick={() => pick('print')}><Icon name="print" size={16} /> {t('print')}</button>
  <a role="menuitem" class="mi-download" download href={dlUrl} onclick={onclose}><Icon name="down" size={16} /> {t('downloadEml')}</a>
  <a role="menuitem" class="mi-source" target="_blank" rel="noopener" href={srcUrl} onclick={onclose}><Icon name="draft" size={16} /> {t('viewSource')}</a>
</div>

<style>
  /* svelte-rwh28b (Reader) — More menu */
  .menu-sep {
    height: 1px;
    background: var(--border-subtle);
    margin: var(--space-1) 0
  }

  .menu-scrim {
    position: fixed;
    inset: 0;
    z-index: 8;
    background: transparent
  }

  .menu {
    position: fixed;
    z-index: 9;
    width: 220px;
    overflow-y: auto;
    overscroll-behavior: contain;
    background: var(--surface-card);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    display: grid;
    padding: var(--space-1)
  }

  .menu button,
  .menu a {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3);
    min-block-size: var(--control-lg);
    border-radius: var(--radius-xs);
    color: var(--text-body);
    font-size: var(--text-sm);
    text-align: start;
    text-decoration: none
  }

  .menu button:hover,
  .menu a:hover {
    background: var(--surface-sunken)
  }

  .mi-star.on {
    color: var(--warning-500)
  }
</style>
